import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {newGuid} from '../../pipes';
import {useTheme} from '../../theme';
import {Theme} from '../../theme/interface';
import {useIsDesktop} from '../../hooks/useIsDesktop';

interface Modal {
  id: string;
  render(params: Modal, methods: ModalMethods): React.ReactNode;
  mobile?: boolean;
  allowBodyScroll?: boolean;
  hideCloseButton?: boolean;
  hasOverlay?: boolean;
  allowClicksThroughOverlay?: boolean;
}

export interface ModalMethods {
  clear(): void;
  pop(): void;
  remove(id: string): void;
  show(modal: Omit<Modal, 'id'>): string;
  replace(modal: Omit<Modal, 'id'>): string;
  getLast(): Modal | null;
}

interface ModalControllerProps {
  modalStack: Modal[];
  setModalStack: React.Dispatch<React.SetStateAction<Modal[]>>;
  isDesktop: boolean;
  isWeb: boolean;
  theme: Theme;
  children?: React.ReactNode;
}

class ModalController extends React.PureComponent<ModalControllerProps> implements ModalMethods {
  clear() {
    this.props.setModalStack(() => []);
  }
  pop() {
    this.props.setModalStack((stack) => stack.slice(0, -1));
  }
  remove(id: string) {
    this.props.setModalStack((stack) => stack.filter((m) => m.id !== id));
  }
  show(modal: Omit<Modal, 'id'>) {
    const id = newGuid();
    this.props.setModalStack((stack) => [...stack, {id, ...modal}]);
    return id;
  }
  replace(modal: Omit<Modal, 'id'>) {
    const id = newGuid();
    this.props.setModalStack(() => [{id, ...modal}]);
    return id;
  }
  getLast(): Modal | null {
    const displayedModalStack = this.props.modalStack.filter((item) => {
      return !this.props.isDesktop ? !!item.mobile : true;
    });

    if (displayedModalStack.length === 0) {
      return null;
    }

    return displayedModalStack[displayedModalStack.length - 1];
  }

  render = () => {
    const {theme, isWeb} = this.props;
    const activeModal = this.getLast();

    const backgroundColor = activeModal?.hasOverlay ? theme.color.grayMenu : undefined;
    const RootComponent = isWeb ? View : SafeAreaProvider;

    return (
      <ModalContext.Provider value={this}>
        {this.props.children}
        {activeModal && (
          <RootComponent
            style={[StyleSheet.absoluteFill, {backgroundColor}]}
            {...{dataSet: {...(activeModal.allowClicksThroughOverlay ? {'pointer-events': 'none'} : {})}}}>
            {activeModal.render(activeModal, this)}
          </RootComponent>
        )}
      </ModalContext.Provider>
    );
  };
}

const ModalContext = React.createContext<ModalMethods | undefined>(undefined);

export const ModalProvider = ({children}: PropsWithChildren) => {
  const [modalStack, setModalStack] = useState<Modal[]>([]);
  const isDesktop = useIsDesktop();
  const controller = useRef<ModalController>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!isDesktop || !controller.current) {
      return;
    }
    if (controller.current.getLast()?.allowBodyScroll) {
      return;
    }
    const body = document.querySelector('body');
    if (body) {
      if (modalStack.length > 0) {
        body.classList.add('modal-open');
      } else {
        body.classList.remove('modal-open');
      }
    }

    return () => {
      if (body) {
        body.classList.remove('modal-open');
      }
    };
  }, [modalStack, isDesktop]);
  useEffect(() => {
    if (!isDesktop) {
      setModalStack((prev) => prev.filter((m) => !!m.mobile));
    }
  }, [isDesktop]);

  return (
    <ModalController
      modalStack={modalStack}
      setModalStack={setModalStack}
      isDesktop={isDesktop}
      isWeb={Platform.OS === 'web'}
      ref={controller}
      theme={theme}>
      {children}
    </ModalController>
  );
};

export const useModal = () => React.useContext(ModalContext) as ModalMethods;
